import { Imports } from '@gleamer/types';
import classNames from 'classnames';
import React from 'react';
import { formatDateTime } from './format';

type ImportBatchStatus = Imports.ImportBatchStatus;
type ImportLineStatus = Imports.ImportLineStatus;
type ImportFileStatus = Imports.ImportFileStatus;

// under way: DeepHealth's lilac and plum, not blue
const BLUE = 'bg-dl-tint text-dl-accent';
const GREEN = 'bg-dl-success-tint text-dl-success';
const RED = 'bg-dl-danger-tint text-dl-danger';
const GRAY = 'bg-dl-track text-dl-text';

const BATCH: Record<ImportBatchStatus, { label: string; style: string; meaning: string }> = {
  PLANNING: {
    label: 'Planning',
    style: BLUE,
    meaning: 'The orchestrator is listing the lines and sending their files.',
  },
  IMPORTING: { label: 'Importing', style: BLUE, meaning: 'Files are being imported.' },
  EXPORTING: {
    label: 'Exporting',
    style: BLUE,
    meaning: 'Every file has an import outcome; some imported files are waiting for BigQuery.',
  },
  STALLED: {
    label: 'Stalled',
    style: 'bg-dl-warning-tint text-dl-warning',
    meaning:
      'Not finished, and nothing has moved for a while: a file may be stuck, or a consumer not running.',
  },
  COMPLETED: {
    label: 'Completed',
    style: GREEN,
    meaning: 'Every file exported to BigQuery or already on the platform.',
  },
  COMPLETED_WITH_ERRORS: {
    label: 'Completed with errors',
    style: 'bg-dl-warning-tint text-dl-warning',
    meaning: 'Every file settled, but some failed or a line was not found.',
  },
  FAILED: {
    label: 'Failed',
    style: RED,
    meaning: 'The planning failed; its error says why. A retry resumes it.',
  },
  CANCELLED: {
    label: 'Cancelled',
    style: GRAY,
    meaning: 'Cancelled by a data manager; a retry or a re-import resumes it.',
  },
};

const LINE: Record<ImportLineStatus, { label: string; style: string }> = {
  TO_DO: { label: 'To do', style: GRAY },
  ON_GOING: { label: 'On going', style: BLUE },
  DONE: { label: 'Done', style: GREEN },
  ERROR: { label: 'Error', style: RED },
};

const FILE: Record<ImportFileStatus, { label: string; style: string }> = {
  TO_DO: { label: 'To do', style: GRAY },
  IMPORTED: { label: 'Imported', style: BLUE },
  EXPORTED: { label: 'Exported', style: GREEN },
  IMPORT_ERROR: { label: 'Import error', style: RED },
  EXPORT_ERROR: { label: 'Export error', style: 'bg-dl-warning-tint text-dl-warning' },
};

export function importStatusLabel(status: ImportBatchStatus): string {
  return BATCH[status]?.label ?? status;
}

export function lineStatusLabel(status: ImportLineStatus): string {
  return LINE[status]?.label ?? status;
}

export function fileStatusLabel(status: ImportFileStatus): string {
  return FILE[status]?.label ?? status;
}

function Badge({ label, style, title }: { label: string; style?: string; title?: string }) {
  return (
    <span
      title={title}
      className={classNames(
        'inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-bold',
        style ?? GRAY
      )}
    >
      {label}
    </span>
  );
}

/** A batch's status, with what it means — and, when stalled, since when — on hover. */
export function ImportStatusBadge({
  status,
  progressAt,
}: {
  status: ImportBatchStatus;
  progressAt?: string | null;
}) {
  const entry = BATCH[status];
  const title =
    status === 'STALLED' && progressAt
      ? `${entry.meaning} Last activity: ${formatDateTime(progressAt)}.`
      : entry?.meaning;
  return (
    <Badge
      label={importStatusLabel(status)}
      style={entry?.style}
      title={title}
    />
  );
}

export function LineStatusBadge({
  status,
  notFound,
}: {
  status: ImportLineStatus;
  notFound?: boolean;
}) {
  return (
    <Badge
      label={notFound ? 'Not found' : lineStatusLabel(status)}
      style={LINE[status]?.style}
      title={notFound ? 'Listed, and nothing under this prefix.' : undefined}
    />
  );
}

export function FileStatusBadge({ status }: { status: ImportFileStatus }) {
  return (
    <Badge
      label={fileStatusLabel(status)}
      style={FILE[status]?.style}
    />
  );
}
